import { Badge, Button, Dialog, Field, Input } from '@cloudflare/kumo';
import { OrgSecurityCard } from './OrgSecurityCard';
import { useEffect, useState } from 'react';
import { palette } from '../lib/palette';
import { ICONS } from './ui/icons';
import { useAuth } from '../contexts/AuthContext';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useAppToast } from '../hooks/useAppToast';

interface Organization {
  id: string;
  name: string;
  slug: string;
  planStatus: string;
  trialEndsAt?: string | null;
  createdAt: string;
  plan: { name: string; displayName?: string } | null;
  usage: { users: number; contacts: number; campaigns: number; connections: number };
  quota: { maxUsers: number; maxContacts: number; maxCampaigns: number; maxConnections: number };
}

const PLAN_STATUS: Record<string, { label: string; color: 'green' | 'orange' | 'red' | 'neutral' }> = {
  ACTIVE: { label: 'Ativo', color: 'green' },
  TRIAL: { label: 'Período de teste', color: 'orange' },
  SUSPENDED: { label: 'Suspenso', color: 'red' },
  CANCELLED: { label: 'Cancelado', color: 'red' },
};

/**
 * Aba "Geral" das Configurações — equivalente ao "Project Settings" do Atlas:
 * cartões com o ID (copiável), o nome e o plano da organização.
 */
export function OrganizationGeneralTab() {
  const { user } = useAuth();
  const toast = useAppToast();
  const authenticatedFetch = useAuthenticatedFetch();
  const [org, setOrg] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const isSuperAdmin = user?.role === 'SUPERADMIN';

  const load = async () => {
    try {
      const res = await authenticatedFetch('/api/tenants/my-organization');
      const data = await res.json();
      if (res.ok) setOrg(data.organization);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyId = async () => {
    if (!org) return;
    try {
      await navigator.clipboard.writeText(org.id);
      toast.success('ID copiado');
    } catch {
      toast.error('Não foi possível copiar');
    }
  };

  const saveName = async () => {
    if (!org || !renaming?.trim()) return;
    setSaving(true);
    try {
      const res = await authenticatedFetch(`/api/tenants/${org.id}`, { method: 'PUT', body: JSON.stringify({ name: renaming.trim() }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || data.error || 'Erro ao renomear');
      toast.success('Nome atualizado');
      setRenaming(null);
      load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Erro ao renomear');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="org-grid">{[1, 2, 3].map((i) => <div key={i} className="org-card" style={{ height: 140, background: palette.gray.light3 }} />)}</div>;
  }
  if (!org) {
    return <div style={{ color: palette.gray.dark1 }}>Não foi possível carregar os dados da organização.</div>;
  }

  const status = PLAN_STATUS[org.planStatus] ?? { label: org.planStatus, color: 'neutral' as const };

  return (
    <>
      <div className="org-grid">
        <div className="org-card">
          <div className="org-card__header">
            <h3 className="text-lg font-semibold org-card__title">ID da organização</h3>
            <Button variant="ghost" shape="square" icon={ICONS.Copy} aria-label="Copiar ID da organização" title={'Copiar'} onClick={copyId} />
          </div>
          <div className="org-card__mono">{org.id}</div>
        </div>

        <div className="org-card">
          <div className="org-card__header">
            <h3 className="text-lg font-semibold org-card__title">Nome da organização</h3>
            {isSuperAdmin && (
              <Button variant="ghost" shape="square" icon={ICONS.Edit} aria-label="Editar nome da organização" title={'Editar'} onClick={() => setRenaming(org.name)} />
            )}
          </div>
          <div>{org.name}</div>
          <div className="text-kumo-subtle text-xs" style={{ marginTop: 4 }}>Identificador: {org.slug}</div>
        </div>

        <div className="org-card">
          <div className="org-card__header">
            <h3 className="text-lg font-semibold org-card__title">Plano</h3>
            <Badge variant={status.color}>{status.label}</Badge>
          </div>
          <div>{org.plan?.displayName || org.plan?.name || 'Sem plano definido'}</div>
          <div className="text-kumo-subtle text-xs" style={{ marginTop: 4 }}>
            Organização criada em {new Date(org.createdAt).toLocaleDateString('pt-BR')}
            {org.planStatus === 'TRIAL' && org.trialEndsAt && ` · teste até ${new Date(org.trialEndsAt).toLocaleDateString('pt-BR')}`}
          </div>
        </div>
      </div>

      <div className="org-card" style={{ marginTop: 24 }}>
        <h3 className="text-lg font-semibold org-card__title" style={{ marginBottom: 16 }}>Uso do plano</h3>
        <div className="tenant-usage" style={{ minWidth: 0 }}>
          {[
            { label: 'Usuários', current: org.usage.users, max: org.quota.maxUsers },
            { label: 'Contatos', current: org.usage.contacts, max: org.quota.maxContacts },
            { label: 'Campanhas interativas', current: org.usage.campaigns, max: org.quota.maxCampaigns },
            { label: 'Conexões', current: org.usage.connections, max: org.quota.maxConnections },
          ].map((s) => {
            const pct = s.max > 0 ? Math.min(100, Math.round((s.current / s.max) * 100)) : 0;
            const color = pct >= 90 ? palette.red.base : pct >= 70 ? palette.yellow.base : palette.green.base;
            return (
              <div key={s.label} className="tenant-usage__item">
                <div className="flex justify-between gap-2">
                  <span>{s.label}</span>
                  <span className="tenant-usage__value">{s.current.toLocaleString('pt-BR')}<span> / {s.max.toLocaleString('pt-BR')}</span></span>
                </div>
                <div className="tenant-usage__bar"><div style={{ width: `${Math.max(pct, s.current > 0 ? 2 : 0)}%`, background: color }} /></div>
              </div>
            );
          })}
        </div>
      </div>

      <OrgSecurityCard />

      <Dialog.Root open={renaming !== null} onOpenChange={(o: boolean) => { if (!o) (() => setRenaming(null))(); }}>
<Dialog size="sm" className="p-6">


        <h3 className="text-lg font-semibold" style={{ marginBottom: 16 }}>Nome da organização</h3>
        <form onSubmit={(e) => { e.preventDefault(); saveName(); }}>
          <Field label="Nome">
            <Input onChange={(e) => setRenaming(e.target.value)} value={renaming ?? ''} autoFocus />
          </Field>
          <div className="flex justify-end gap-2" style={{ marginTop: 24 }}>
            <Button variant="secondary" type="button" onClick={() => setRenaming(null)}>Cancelar</Button>
            <Button type="submit" variant="primary" loading={saving} disabled={!renaming?.trim()}>Salvar</Button>
          </div>
        </form>
      
</Dialog>
</Dialog.Root>
    </>
  );
}
